import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useCurrency } from '../hooks/useCurrency';
import { useEffect, useState } from 'react';
import { AccountMenu } from './AccountMenu';
import { OnlineBadge } from './OnlineBadge';
import { Stars } from './Stars';
import { useCart } from '../hooks/useCart';
import { api } from '../api/client';
import type { ResumoFeedbacks } from '../api/types';

/** Sacola com a contagem. Some quando está vazia, para não virar enfeite. */
function SacolaLink() {
  const { quantidade } = useCart();
  if (quantidade === 0) return null;
  return (
    <Link className="btn ghost small sacola-link" to="/sacola" title="Ver a sacola">
      🛒 <b>{quantidade}</b>
    </Link>
  );
}

/**
 * Nota da loja no cabeçalho, para qualquer visitante.
 *
 * É a reputação onde ela vale: na primeira tela, antes de escolher a skin —
 * e não escondida atrás do menu da conta, que só quem já entrou abre.
 *
 * Três estados, e a diferença entre eles é o ponto:
 *
 *  - carregando: um vulto do mesmo tamanho, para o cabeçalho não pular quando
 *    a nota chega;
 *  - sem avaliação: nenhuma estrela. Cinco contornos apagados leem como "nota
 *    zero" à distância, que é o contrário do que uma loja nova quer dizer —
 *    então aqui vira convite, não placar;
 *  - com nota: estrelas, o número e a contagem, que é o que dá lastro ao
 *    número.
 *
 * Puxa só o resumo, nunca o mural: são dois números, e o cabeçalho aparece em
 * toda página.
 */
function NotaDaLoja() {
  const [summary, setSummary] = useState<ResumoFeedbacks | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    void api
      .resumoFeedbacks()
      .then((r) => setSummary(r.summary))
      .catch(() => setSummary(null))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) {
    return <span className="nota-loja vazia" aria-hidden />;
  }

  const nota = summary && summary.total > 0 && summary.average !== null ? summary : null;

  if (!nota) {
    return (
      <Link className="nota-loja convite" to="/feedbacks">
        <IconeSelo />
        <span>Avaliações</span>
      </Link>
    );
  }

  const media = nota.average!;
  return (
    <Link
      className="nota-loja"
      to="/feedbacks"
      aria-label={`Avaliação da loja: ${media.toFixed(1).replace('.', ',')} de 5 em ${nota.total} ${
        nota.total === 1 ? 'avaliação' : 'avaliações'
      }`}
    >
      <Stars value={media} size="sm" />
      <b className="nota-loja-valor">{media.toFixed(1).replace('.', ',')}</b>
      <span className="nota-loja-sep" aria-hidden />
      <span className="nota-loja-total">
        {nota.total} <span className="nota-loja-palavra">{nota.total === 1 ? 'avaliação' : 'avaliações'}</span>
      </span>
    </Link>
  );
}

/** Selo de aspas: diz "opinião de cliente" sem fingir uma nota que não existe. */
function IconeSelo() {
  return (
    <svg className="nota-loja-selo" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.8-.9L3 21l1.4-4.2A8.5 8.5 0 1 1 21 12z"
      />
      <path
        fill="currentColor"
        d="M9.6 9.1c-1.2 0-2.1.9-2.1 2s.9 2 2.1 2c.2 0 .4 0 .5-.1-.2.7-.8 1.2-1.5 1.4l.4 1c1.6-.4 2.7-1.8 2.7-3.5 0-1.6-.9-2.8-2.1-2.8zm5.1 0c-1.2 0-2.1.9-2.1 2s.9 2 2.1 2c.2 0 .3 0 .5-.1-.2.7-.8 1.2-1.5 1.4l.4 1c1.6-.4 2.7-1.8 2.7-3.5 0-1.6-.9-2.8-2.1-2.8z"
      />
    </svg>
  );
}

export function Header() {
  const { t, lang, setLang } = useI18n();
  // Tamanho do catálogo, saúde da integração, cotação e escolha de moeda são
  // informação de operação: o cliente não tem o que fazer com elas, e a
  // cotação em yuan ainda entrega o preço de custo.
  const { currency, setCurrency } = useCurrency();

  // O site inteiro é em reais. Sem seletor na tela, quem tiver "¥" salvo do
  // tempo em que ele existia ficaria preso em yuan para sempre.
  useEffect(() => {
    if (currency !== 'BRL') setCurrency('BRL');
  }, [currency, setCurrency]);

  return (
    <header className="topbar">
      <Link to="/" className="brand" aria-label={t('app.name')}>
        {/* A logo já traz o nome; o alt cobre quem usa leitor de tela. A
            versão 2x entra em telas retina sem pesar nas comuns. */}
        <img
          className="brand-logo"
          src="/logo.png"
          srcSet="/logo.png 1x, /logo-2x.png 2x"
          alt={t('app.name')}
          width={224}
          height={76}
        />
        <span className="brand-tagline">{t('app.tagline')}</span>
      </Link>

      <div className="topbar-right">
        <NotaDaLoja />

        <div className="switch" role="group" aria-label={t('header.language')}>
          <button className={lang === 'pt' ? 'active' : ''} onClick={() => setLang('pt')} title="Português (Brasil)">
            PT
          </button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')} title="English">
            EN
          </button>
        </div>

        <OnlineBadge />
        <SacolaLink />
        <AccountMenu />
      </div>
    </header>
  );
}
